import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { WarningBanner } from 'ui/banner';
import {
  CHANGE_USER_PROJECT_AND_JOURNEY_REQUEST,
  SAVE_AND_SWITCH_REQUEST,
} from 'core/userContext/actionTypes';

import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { selectProjects } from 'core/userContext';
import { Modal, ModalHeader, ModalBody, ModalFooter, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import {
  closeModal,
  getEdges,
  getNodes,
  getWorkflow,
  getWorkflowHasUnsavedChanges,
  saveWorkflowDraftRequest,
  updateHasUnsavedChanges,
} from '../../../ducks';
import { SWITCH_PROJECTS_MODAL } from '../../../constants';
import { WorkflowType } from '../../../enums';

export const DONT_SAVE_BUTTON_TEST_ID = 'dont-save-button';
export const SAVE_AND_SWITCH_BUTTON_TEST_ID = 'save-and-switch-button';
export const SWITCH_BUTTON_TEST_ID = 'switch-button';

export interface SwitchProjectsModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Selected workflow id */
  workflowId: number;
  /** Selected workflow type */
  workflowType: WorkflowType;
  /** Selected project id */
  projectId: number;
}

export const SwitchProjectsModal: React.FC<React.PropsWithChildren<SwitchProjectsModalProps>> = ({
  workflowId,
  workflowType,
  projectId,
  isOpen,
}) => {
  const dispatch = useDispatch();
  const workflow = useSelector(getWorkflow);
  const hasUnsavedChanges = useSelector(getWorkflowHasUnsavedChanges);
  const nodes = useSelector(getNodes);
  const edges = useSelector(getEdges);
  const projects = useSelector(selectProjects);
  const destinationProjectName = projects?.find((p) => p.id === projectId)?.name;
  const currentWorkflowType = workflow?.workflowType;
  // should only show if there are unsaved changes and in draft view since changes should not be made
  // outside of draft view in our current journeys studio
  const shouldShowUnsavedChangesButtons = hasUnsavedChanges && currentWorkflowType === 'Draft';
  const saveWorkflowAction = saveWorkflowDraftRequest({ ...workflow, nodes, edges });
  const handleCloseModal = () => dispatch(closeModal(SWITCH_PROJECTS_MODAL));

  const handleGoToJourney = () => {
    dispatch({
      type: CHANGE_USER_PROJECT_AND_JOURNEY_REQUEST,
      projectId,
      workflowId,
      workflowType,
    });
    handleCloseModal();
  };

  const handleSaveAndGoToPublished = () => {
    dispatch({
      type: SAVE_AND_SWITCH_REQUEST,
      saveWorkflowAction,
      projectId,
      workflowId,
      workflowType,
    });
  };

  const [waitForUnsavedChanges, setWaitForUnsavedChanges] = useState(false);

  useEffect(() => {
    if (!hasUnsavedChanges && waitForUnsavedChanges) handleGoToJourney();
  }, [hasUnsavedChanges]);

  return (
    <Modal
      id={SWITCH_PROJECTS_MODAL}
      isModalOpen={isOpen}
      onClose={handleCloseModal}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.WARNING}
    >
      <ModalHeader autoFocus={false}>Switch projects?</ModalHeader>
      <ModalBody>
        <p>
          To view this journey, you need to be in the <strong>{destinationProjectName}</strong>{' '}
          project. Do you want to switch projects?
        </p>
        <WarningBanner Icon={false} title="Heads up!">
          If you have Iterable open in any other tabs, this will switch you to{' '}
          {destinationProjectName} there too.
        </WarningBanner>
      </ModalBody>
      <ModalFooter data-test={`${SWITCH_PROJECTS_MODAL}-${workflowId}-actions`}>
        {shouldShowUnsavedChangesButtons ? (
          <>
            <Button
              data-test={DONT_SAVE_BUTTON_TEST_ID}
              onClick={() => {
                setWaitForUnsavedChanges(true);
                // update the journey state so the modal does not appear. kinda hacky.
                dispatch(updateHasUnsavedChanges(false));
              }}
              size={BUTTON_SIZES.LARGE}
              variant={BUTTON_VARIANTS.SECONDARY}
            >
              Leave without saving
            </Button>
            <Button
              data-test={SAVE_AND_SWITCH_BUTTON_TEST_ID}
              onClick={handleSaveAndGoToPublished}
              size={BUTTON_SIZES.LARGE}
              variant={BUTTON_VARIANTS.PRIMARY}
            >
              Save and switch
            </Button>
          </>
        ) : (
          <Button
            data-test={SWITCH_BUTTON_TEST_ID}
            onClick={handleGoToJourney}
            size={BUTTON_SIZES.LARGE}
            variant={BUTTON_VARIANTS.PRIMARY}
          >
            Switch projects
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};
